/* A workspace skill opened for editing: its detail and every file's bytes, read once
   into the editor's starting folder. The editor owns the work from there (a form's
   state, not the server's); saving makes a new version. */
import { useQuery } from "@tanstack/react-query";

import { getSkill, getSkillFile, skillsKeys } from "@/shared/api/skills";

import { readText, splitSkillMd, type DraftFile, type SkillMd } from "../folder";

import type { SkillDetail } from "@/shared/types";

export interface SkillToEdit {
  skill: SkillDetail;
  md: SkillMd;
  /** SKILL.md as stored: saved as it is while its fields are unchanged. */
  skillMd: string;
  /** Every file but SKILL.md. */
  files: DraftFile[];
}

async function readSkillToEdit(id: string): Promise<SkillToEdit> {
  const skill = await getSkill(id);
  const files = await Promise.all(
    skill.files.map(async (f): Promise<DraftFile> => {
      const { blob } = await getSkillFile(id, f.path);
      const text = await readText(blob);
      return { path: f.path, text, blob: text == null ? blob : null, size: blob.size };
    }),
  );
  const md = files.find((f) => f.path === "SKILL.md");
  const skillMd = md?.text ?? "";
  // the name and description as the server read them; the rest of SKILL.md as written
  return {
    skill,
    md: { name: skill.slug, description: skill.description, ...splitSkillMd(skillMd) },
    skillMd,
    files: files.filter((f) => f !== md),
  };
}

export function useSkillToEdit(id: string | undefined) {
  return useQuery({
    queryKey: skillsKeys.toEdit(id ?? ""),
    queryFn: () => readSkillToEdit(id as string),
    enabled: !!id,
    staleTime: Infinity, // read once; Reopen refetches it on purpose
    gcTime: 0,
    meta: { silent: true },
  });
}
